import apiClient from './apiClient';
import config from '../config';

/**
 * Get member summary.
 * Uses SSO Bearer token from apiClient (wired in by ProtectedApp).
 * @param {string} memberId    - Member ID to fetch summary for
 * @param {string} [locationCode] - Optional location code; appended as ?location-code=...
 * @returns {Promise<object>} - Member summary information
 */
export async function getMemberSummary(memberId, locationCode) {
  const base = `${config.operaExpBaseUrl}/v1/member/${memberId}/summary`;
  const url  = locationCode ? `${base}?location-code=${encodeURIComponent(locationCode)}` : base;
  return apiClient.get(url);
}

/**
 * Resolve a pmsHotelId to its locationCode and locationLegalName.
 * Uses SSO Bearer token from apiClient (wired in by ProtectedApp).
 * @param {string} pmsHotelId - PMS Hotel ID 
 * @returns {Promise<object>} - Location object ({ locationCode, locationLegalName, ... })
 */
export async function getLocation(pmsHotelId) {
  const url = `${config.operaExpBaseUrl}/v1/location/${pmsHotelId}`;
  return apiClient.get(url);
}

/**
 * Get the full list of locations / properties.
 * GET /v1/location
 * @returns {Promise<Array>} - Array of { locationCode, locationLegalName, pmsHotelId }
 */
export async function getLocations() {
  const url = `${config.operaExpBaseUrl}/v1/location`;
  return apiClient.get(url);
}

/**
 * Get member loyalty information.
 * Uses SSO Bearer token from apiClient (wired in by ProtectedApp).
 * @param {string} memberId - Member ID to fetch loyalty info for
 * @returns {Promise<object>} - Loyalty information
 */
export async function getMemberLoyalty(memberId) {
  const url = `${config.operaExpBaseUrl}/v1/member/${memberId}/loyalty`;
  return apiClient.get(url);
}

/**
 * Get member comments with optional filters.
 * @param {string} memberId
 * @param {object} [params] - { propcode, priority, commentSource, commentStatus, fromDate, toDate }
 * @returns {Promise<Array>} - Array of comment objects
 */
export async function getMemberComments(memberId, params = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.append(k, v);
  });
  const query = qs.toString();
  const url = `${config.operaExpBaseUrl}/v1/member/${memberId}/comments${query ? `?${query}` : ''}`;
  return apiClient.get(url);
}

/**
 * Add a new comment for a member.
 * POST /v1/member/:memberId/comments
 * @param {string} memberId
 * @param {object} body - { comment, priority, department, limitToProperty, commentType, expireDate, commentSource, sourceProperty, createdBy }
 * @returns {Promise<object>}
 */
export async function postMemberComment(memberId, body) {
  const url = `${config.operaExpBaseUrl}/v1/member/${memberId}/comments`;
  return apiClient.post(url, body);
}

/**
 * Update an existing comment for a member.
 * PUT /v1/member/:memberId/comments/:commentId
 * @param {string} memberId
 * @param {string|number} commentId
 * @param {object} body - same shape as postMemberComment body
 * @returns {Promise<object>}
 */
export async function putMemberComment(memberId, commentId, body) {
  const url = `${config.operaExpBaseUrl}/v1/member/${memberId}/comments/${commentId}`;
  return apiClient.put(url, body);
}


